import { useState, useEffect } from 'react'
import fetch from '@adc/fetch'
import * as blueprint from '@adc/blueprint'

/**
 * Hook that fetches field_change_timeline from the graph API.
 * Returns the raw FieldChangeTimeline[] response — each entry contains
 * { field, changes: [{ changed_at, changed_by, previous_value, current_value, delta_label }], total_changes }
 *
 * @param {string} entityType - GraphQL type name (e.g. 'listing', 'property', 'organization')
 * @param {string} entityId - Entity ID
 * @param {object} options
 * @param {string} options.period - Lookback period (default: '365d')
 * @param {string[]} options.fields - Fields to query (default: ['*'] for all)
 * @returns {{ timelines: Array, loading: boolean, error: Error|null }}
 */
// Map entity type to the GraphQL query argument name
const ENTITY_ARG_MAP = {
  listing: 'listingId',
  property: 'propertyId',
  venue: 'venueId',
  event: 'eventCode',
  party: 'partyId',
  user: 'userId',
  organization: 'organizationId',
  venue_template: 'venueId',
}

const useFieldChangeTimeline = (entityType, entityId, options = {}) => {
  const { period = '365d', fields = ['*'] } = options
  const [timelines, setTimelines] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!entityType || !entityId) return

    let cancelled = false
    setLoading(true)
    setError(null)

    const argName = ENTITY_ARG_MAP[entityType] || `${entityType}Id`
    const queryType = entityType === 'venue_template' ? 'venue' : entityType

    const query = `
      query ${blueprint.utils.embeddableOperation(
        'uiMorpheus_fieldChangeTimeLine'
      )}($id: ID!, $fields: [String!]!, $period: String!) {
        ${queryType}(${argName}: $id) {
          field_change_timeline(fields: $fields, period: $period) {
            field
            changes {
              changed_at
              changed_by
              previous_value
              current_value
              delta_label
            }
            total_changes
          }
        }
      }
    `

    fetch
      .forAuctionGraph({
        query,
        variables: { id: entityId, fields, period },
      })
      .then(res => {
        if (cancelled) return undefined
        setTimelines(res?.body?.data?.[queryType]?.field_change_timeline || [])
        setLoading(false)
        return undefined
      })
      .catch(err => {
        if (cancelled) return undefined
        setError(err)
        setLoading(false)
        return undefined
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, period, fields.join(',')])

  return { timelines, loading, error }
}

export default useFieldChangeTimeline
